import React, { useState, useEffect } from "react";

import CardRightV2 from "../cardRightV2";
import LogoLoader from "../LogoLoader";
import ButtonV2 from "../button/button_v2";
import api from "../../services/api";
import * as S from "./styles";
import { BodyM } from "../../styles/stylesTypography";

/**
 * Drawer lateral que exibe o dossiê de uma praga.
 * Busca os dados de /pragas/:pestId/dossie ao abrir.
 *
 * Props:
 *  - pestId   {number|string}  ID da praga em agro_pests
 *  - pestName {string}         Nome da praga (exibido no cabeçalho)
 *  - isOpen   {boolean}
 *  - onClose  {function}
 */
const PragaDossieDrawer = ({ pestId, pestName, isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      return;
    }
    if (!pestId) {
      setData(null);
      return;
    }

    setLoading(true);
    api
      .get(`/pragas/${pestId}/dossie`)
      .then((res) => setData(res.data?.data ?? null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isOpen, pestId]);

  const hasData =
    data &&
    (data.nome_cientifico ||
      data.descricao ||
      data.sintomas ||
      data.classificacao ||
      data.bioecologia ||
      data.controle ||
      data.ciclo ||
      data.familia ||
      data.observacoes);

  const Field = ({ label, value }) => {
    if (!value) return null;
    return (
      <S.Field>
        <BodyM fontWeight={700}>{label}</BodyM>
        <S.FieldValue>{value}</S.FieldValue>
      </S.Field>
    );
  };

  return (
    <CardRightV2
      open={isOpen}
      updateIsOpen={(open) => {
        if (!open) onClose();
      }}
      title={pestName || "Dossiê da Praga"}
    >
      <S.Container>
        {loading ? (
          <S.CenterLoader>
            <LogoLoader id="dossie-loader" color="#2FBB69" />
          </S.CenterLoader>
        ) : !hasData ? (
          <S.Empty>
            <S.EmptyText>
              Ainda não possuímos dados suficientes sobre essa praga.
            </S.EmptyText>
          </S.Empty>
        ) : (
          <>
            {/* campos comuns */}
            <Field label="Nome Científico"       value={data.nome_cientifico} />
            <Field label="Nomes Vulgares"        value={data.nome_vulgar} />
            <Field label="Autor da Descrição"    value={data.autor_descricao} />

            {/* inseto / doença */}
            <Field label="Classificação"         value={data.classificacao} />
            <Field label="Cultura"               value={data.cultura} />
            <Field label="Descrição"             value={data.descricao} />
            <Field label="Sintomas"              value={data.sintomas} />
            <Field label="Bioecologia"           value={data.bioecologia} />
            <Field label="Controle"              value={data.controle} />

            {/* planta daninha */}
            <Field label="Família"               value={data.familia} />
            <Field label="Regiões de Ocorrência" value={data.regioes_ocorrencia} />
            <Field label="Divisão/Classe"        value={data.divisao_classe} />
            <Field label="Ciclo"                 value={data.ciclo} />
            <Field label="Reprodução/Propagação" value={data.reproducao_propagacao} />
            <Field label="Habitat/Tipologia"     value={data.habitat_tipologia} />
            <Field label="Adaptações"            value={data.adaptacoes} />
            <Field label="Altura (cm)"           value={data.altura_cm} />
            <Field label="Filotaxia"             value={data.filotaxia} />
            <Field label="Forma do Limbo"        value={data.forma_limbo} />
            <Field label="Superfície"            value={data.superficie} />
            <Field label="Consistência"          value={data.consistencia} />
            <Field label="Nervação/Venação"      value={data.nervacao_venacao} />
            <Field label="Comprimento da Folha"  value={data.comprimento_folha} />
            <Field label="Inflorescência"        value={data.inflorescencia} />
            <Field label="Perianto"              value={data.perianto} />
            <Field label="Tipologia do Fruto"    value={data.tipologia_fruto} />
            <Field label="Observações"           value={data.observacoes} />

            {/* comum */}
            <Field label="Bibliografia"          value={data.bibliografia} />
          </>
        )}

        <ButtonV2
          value="Ver imagens"
          width="100%"
          height="40px"
          onClick={() => {
            const q = encodeURIComponent(pestName || "");
            window.open(
              `https://www.google.com/search?q=${q} Praga&tbm=isch`,
              "_blank",
              "noopener,noreferrer"
            );
          }}
        />
      </S.Container>
    </CardRightV2>
  );
};

export default PragaDossieDrawer;
